import React, { useState, useEffect, useMemo, createRef } from "react";
import Header from "../components/Header/Header";
import { Chapter, getChapters, ChapterArticle } from "../api/cocApi";
import ChapterHeading from "../components/ChapterHeading/ChapterHeading";
import Card from "../components/Card/Card";
import Grid from "../components/Grid/Grid";
import Container from "../components/Container/Container";
import CompactNav from "../components/CompactNav/CompactNav";
import Media from "react-media";
import PatternBackground from "../components/PatternBackground/PatternBackground";
import MediaTeaser from "../components/MediaTeaser/MediaTeaser";
import { scrollToRef } from "../common/scrollToRef";
import Spacer from "../components/Spacer/Spacer";
import searchStyles from "../common/Search.module.less";
import Button from "../components/Button/Button";

function Home() {
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [results, setResults] = useState<ChapterArticle[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const chapterRefs = useMemo(() => Array.from({ length: 3 }).map(() => createRef<HTMLDivElement>()), []);
    const matchText = (query: string, source: string) => source.search(new RegExp(query, "i")) !== -1;
    const anyMatchText = (query: string, ...sources: string[]) => sources.some(source => matchText(query, source));

    useEffect(() => {
        getChapters().then(chapters => {
            setChapters(chapters);
        });
    }, []);

    useEffect(() => {
        if (!searchQuery || searchQuery === "") {
            return;
        }

        const result = chapters.flatMap(chapter => {
            return chapter.items
                .filter(item =>
                    anyMatchText(
                        searchQuery,
                        item.heading,
                        item.howToAct.map(x => x.answer + x.question).join(),
                        item.introduction,
                        item.keyTakeaways,
                        item.meaning.asEmployee,
                        item.meaning.asLeader,
                        item.relatedMaterial.map(x => x.text).join(),
                        item.standFor
                    )
                )
                .toArray();
        });

        setResults(result as ChapterArticle[]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chapters, searchQuery]);

    return (
        <>
            <Header />
            <Media
                query={{ maxWidth: 600 }}
                render={() => (
                    <CompactNav
                        items={[
                            {
                                title: "Chapter 1",
                                background: "blueDark",
                                onClick: () => {
                                    setSearchQuery("");
                                    setImmediate(() => {
                                        scrollToRef(chapterRefs[0]);
                                    });
                                }
                            },
                            {
                                title: "Chapter 2",
                                background: "blueLight",
                                onClick: () => {
                                    setSearchQuery("");
                                    setImmediate(() => {
                                        scrollToRef(chapterRefs[1]);
                                    });
                                }
                            },
                            {
                                title: "Chapter 3",
                                background: "blueMid",
                                onClick: () => {
                                    setSearchQuery("");
                                    setImmediate(() => {
                                        scrollToRef(chapterRefs[2]);
                                    });
                                }
                            }
                        ]}
                    />
                )}
            />

            <div className={searchStyles.Search}>
                <input
                    type="search"
                    className={[
                        searchStyles["Search-input"],
                        searchQuery.length !== 0 ? searchStyles["Search-input--hasValue"] : null
                    ].join(" ")}
                    placeholder="Search"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
            </div>

            <PatternBackground>
                <Container>
                    {searchQuery ? (
                        <>
                            <ChapterHeading heading={"Search results"} />

                            {results.length > 0 ? (
                                <Grid columns={4}>
                                    {results.map(item => (
                                        <Card
                                            key={item.heading}
                                            backgroundColor={item.chapterColor}
                                            heading={item.heading}
                                            image={item.image}
                                            preHeading={item.tocIndex}
                                            linkTo={item.link}
                                        />
                                    ))}
                                </Grid>
                            ) : (
                                <div className="u-textCenter">
                                    <p>The search query did not match any articles.</p>
                                    <Button theme="grey" onClick={() => setSearchQuery("")}>
                                        View all articles
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        chapters.map((chapter, chapterIndex) => (
                            <div key={chapter.heading} ref={chapterRefs[chapterIndex]}>
                                <ChapterHeading heading={chapter.heading} preHeading={`Chapter ${chapterIndex + 1}`} />

                                <Grid columns={4}>
                                    {chapter.items.toArray().map((item, itemIndex) => (
                                        <Card
                                            key={item.heading}
                                            backgroundColor={item.chapterColor}
                                            heading={item.heading}
                                            image={item.image}
                                            preHeading={item.tocIndex}
                                            linkTo={item.link}
                                        />
                                    ))}
                                </Grid>
                            </div>
                        ))
                    )}
                    <Spacer size="Spacer--large">
                        <MediaTeaser
                            heading="Speak up"
                            body="Suspect a violation of the Code of Conduct? Report it to a manager or through our anonymous Whistleblower function."
                            linkText="Read more"
                            linkTo="/speak-up"
                            image={process.env.PUBLIC_URL + "/assets/speak-up.jpg"}
                        />

                        <MediaTeaser
                            heading="Responsibilities"
                            body="This Code of Conduct applies to all persons working for or on behalf of Business Sweden."
                            linkText="Read more"
                            linkTo="/responsibilities"
                            image={process.env.PUBLIC_URL + "/assets/responsibilities.jpg"}
                            imagePosition="right"
                        />

                        <MediaTeaser
                            heading="Making a difference by doing the right thing"
                            preHeading="A word from our CEO"
                            body="“We have an increased responsibility to do the right thing”<br/>- Jan Larsson<br/>CEO and President"
                            linkText="Read more"
                            linkTo="/word-from-our-ceo"
                            image={process.env.PUBLIC_URL + "/assets/ceo.jpg"}
                        />
                    </Spacer>
                </Container>
            </PatternBackground>
        </>
    );
}

export default Home;
